// Slice H8 — shared safe markdown renderer.
//
// Wraps react-markdown + remark-gfm with:
//   - A theme-aware token API (caller passes a `theme` object — works
//     with both chat's `S` palette and Hermes's cf_theme `tokens()`
//     return value as long as the documented keys are present)
//   - URL-scheme filter blocking `javascript:` and `data:` URLs in
//     anchor hrefs and image srcs (Acceptance Gate #9). Chat's existing
//     MsgContent doesn't have this filter — Hermes is therefore strictly
//     more secure than chat for the markdown render path; chat-side
//     migration is deferred to avoid scope creep in this slice.
//   - NO rehype-raw, NO dangerouslySetInnerHTML (Acceptance Gate #9).
//   - Optional `codeBlockOverride` prop so callers (chat) can plug in
//     their richer code-block UI (Preview/Copy buttons + filename
//     header) without forcing every consumer to rebuild it. When
//     omitted, a minimal monospace block is rendered.
//
// Theme keys consumed (all CSS color strings):
//   text         body text
//   textMuted    muted text in tables / blockquotes
//   accent       links
//   border       table cell + blockquote border
//   borderSubtle hr separator + code header divider
//   codeBlockBg  fenced code block background
//   surfaceAlt   table header row background (optional; falls back to a
//                neutral tint)

import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import CodeBlockView from "./CodeBlockView.jsx";
// URL-scheme filter — pure helper, lives in markdown_safe_url.js so the
// test suite can exercise it without a JSX transpiler (H7.1 pattern).
import { safeUrl } from "./markdown_safe_url.js";

function MarkdownMessage({ text, theme, codeBlockOverride }) {
  if (!text) return null;
  const t = theme || {};
  // Defaults + safe fallbacks for any missing token keys. Lets a caller
  // pass a partial theme without crashing.
  const T = {
    text:         t.text         || "#222",
    textMuted:    t.textMuted    || "#666",
    accent:       t.accent       || "#3b82c4",
    border:       t.border       || "rgba(127,127,127,0.25)",
    borderSubtle: t.borderSubtle || "rgba(127,127,127,0.15)",
    codeBlockBg:  t.codeBlockBg  || "rgba(127,127,127,0.06)",
    surfaceAlt:   t.surfaceAlt   || "rgba(127,127,127,0.08)",
  };

  // Code-block component: caller-provided override OR a minimal default.
  const CodeBlock = useMemo(() => {
    if (typeof codeBlockOverride === "function") return codeBlockOverride;
    return function DefaultCode({ className, children, ...props }) {
      const m = /^language-(.+)$/.exec(className || "");
      if (!m) {
        return (
          <code className="cf-md-inline-code" style={{
            background: T.borderSubtle, padding: "1px 5px",
            borderRadius: 4, fontFamily: "monospace", fontSize: "0.92em",
          }} {...props}>
            {children}
          </code>
        );
      }
      const lang = m[1].toLowerCase();
      const content = String(children).replace(/\n$/, "");
      return (
        <div style={{
          background: T.codeBlockBg, border: `1px solid ${T.border}`,
          borderRadius: 8, margin: "8px 0", overflow: "hidden",
        }}>
          <div style={{
            padding: "3px 12px", borderBottom: `1px solid ${T.borderSubtle}`,
            fontSize: 10, color: T.textMuted, fontFamily: "monospace",
          }}>
            {lang}
          </div>
          <CodeBlockView content={content} className={className} lang={lang} theme={T} />
        </div>
      );
    };
  }, [codeBlockOverride, T.text, T.textMuted, T.border, T.borderSubtle, T.codeBlockBg]);

  return (
    <div style={{ color: T.text, lineHeight: 1.7, fontSize: 13.5, wordBreak: "break-word" }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        // urlTransform runs on every link/image href before the anchor /
        // img is constructed. Returning null here aborts the URL — we
        // belt-and-suspenders this by also filtering in the `a`/`img`
        // overrides below.
        urlTransform={(url) => safeUrl(url) || ""}
        components={{
          // Strip default <pre> wrapper so the `code` override renders
          // the full block UI without double-wrapping.
          pre: ({ children }) => <>{children}</>,
          code: CodeBlock,
          a: ({ href, children, ...props }) => {
            const safe = safeUrl(href);
            if (!safe) {
              // Render the children as plain text — drop the link.
              return <span style={{ color: T.textMuted }}>{children}</span>;
            }
            return (
              <a {...props} href={safe} target="_blank" rel="noopener noreferrer"
                className="cf-md-link"
                style={{ color: T.accent }}>
                {children}
              </a>
            );
          },
          img: ({ src, alt, ...props }) => {
            const safe = safeUrl(src);
            if (!safe) return <span style={{ color: T.textMuted, fontSize: 11 }}>[image: {alt || "blocked"}]</span>;
            return <img {...props} src={safe} alt={alt || ""} className="cf-md-img" style={{ maxWidth: "100%", height: "auto", borderRadius: 6 }} />;
          },
          hr: () => <hr className="cf-md-hr" />,
          ul: ({ children, ...props }) => <ul {...props} className="cf-md-list">{children}</ul>,
          ol: ({ children, ...props }) => <ol {...props} className="cf-md-list">{children}</ol>,
          table: ({ children, ...props }) => (
            <div style={{ overflowX: "auto", margin: "0.5em 0" }}>
              <table {...props} className="cf-md-table" style={{ borderCollapse: "collapse", width: "auto", fontSize: "0.95em" }}>
                {children}
              </table>
            </div>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote {...props} className="cf-md-blockquote">
              {children}
            </blockquote>
          ),
          th: ({ children, ...props }) => (
            <th {...props} style={{
              border: `1px solid ${T.border}`, padding: "5px 9px",
              background: T.surfaceAlt, textAlign: "left", fontWeight: 700,
            }}>
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td {...props} style={{ border: `1px solid ${T.border}`, padding: "5px 9px" }}>
              {children}
            </td>
          ),
          h1: ({ children, ...props }) => <h1 {...props} className="cf-md-heading" style={{ fontSize: "1.4em", fontWeight: 800, margin: "0.6em 0 0.3em", color: T.text }}>{children}</h1>,
          h2: ({ children, ...props }) => <h2 {...props} className="cf-md-heading" style={{ fontSize: "1.2em", fontWeight: 700, margin: "0.5em 0 0.3em", color: T.text }}>{children}</h2>,
          h3: ({ children, ...props }) => <h3 {...props} className="cf-md-heading" style={{ fontSize: "1.05em", fontWeight: 600, margin: "0.4em 0 0.2em", color: T.text }}>{children}</h3>,
          p:  ({ children, ...props }) => <p  {...props} style={{ margin: "0 0 6px" }}>{children}</p>,
          ul: ({ children, ...props }) => <ul {...props} style={{ margin: "4px 0 6px", paddingLeft: 22 }}>{children}</ul>,
          ol: ({ children, ...props }) => <ol {...props} style={{ margin: "4px 0 6px", paddingLeft: 22 }}>{children}</ol>,
          li: ({ children, ...props }) => <li {...props} style={{ margin: "2px 0" }}>{children}</li>,
          blockquote: ({ children, ...props }) => (
            <blockquote {...props} style={{
              borderLeft: `3px solid ${T.border}`, margin: "8px 0",
              padding: "2px 12px", color: T.textMuted,
            }}>
              {children}
            </blockquote>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

export { MarkdownMessage, safeUrl };
export default MarkdownMessage;
